import { normalizeFirstName, normalizeLastName } from '@massilia/features'
import { formatConditionText, parseFeeAmountFromDescription } from '@massilia/queries'
import { Name } from '@massilia/ui'
import { Check, UserCircle, X } from 'lucide-react'
import { toast } from 'sonner'

import { formatDateShort } from '@/utils/format-date'
import { GENDER_LABELS_FULL } from '@/utils/gender-labels'
import { derivePassengerType } from '@/utils/passenger-type'

// ── Helpers ──

const PASSENGER_TYPE_DISPLAY = new Map<string, string>([
  ['Adult', 'Adulte'],
  ['Child', 'Enfant'],
  ['Infant', 'Bébé'],
])

export interface FlightSegmentCardProps {
  airline: string
  airlineLogoUrl?: null | string
  arrivalLabel: string
  departureLabel: string
  durationLabel: string
  journeyTypeLabel?: null | string
  route: string
  stopsLabel: string
  travelClass?: null | string
}

// ── PassengerCard ──

export interface PassengerCardProps {
  firstName: string
  lastName: string
  /** ISO date string */
  birthDate?: null | string
  gender?: null | string
  /** Explicit type. If omitted, derived from birthDate when available. */
  type?: 'Adult' | 'Child' | 'Infant' | null
}

export interface TicketConditionsProps {
  conditions: {
    exchangeable: { allowed: boolean; feeDescription: string }
    refundable: { allowed: boolean; feeDescription: string }
  }
  debugRule?: {
    ruleCode: string
    ruleId: string
    ruleSource: 'API_ONLY' | 'FALLBACK_M999' | 'OVERRIDE'
  }
  /**
   * Nombre de bébés (< 2 ans) du dossier. Quand > 0, la mention MOD-INFANT
   * « — 25 € pour un bébé (- 2 ans) » est ajoutée à chaque ligne de conditions.
   */
  infantCount?: null | number
}

// ── FlightSegmentCard ──

export function FlightSegmentCard({
  airline,
  airlineLogoUrl,
  arrivalLabel,
  departureLabel,
  durationLabel,
  journeyTypeLabel,
  route,
  stopsLabel,
  travelClass,
}: FlightSegmentCardProps) {
  return (
    <div className="flex flex-col items-start rounded-lg border border-gray-light-200 bg-white px-4 py-3">
      {/* Carrier */}
      {airlineLogoUrl ? (
        <div className="flex h-15.25 w-20.75 items-center justify-center p-4">
          <img
            alt={airline}
            className="max-h-full max-w-full object-contain"
            src={airlineLogoUrl}
          />
        </div>
      ) : (
        <p className="px-1 py-3 text-xs font-medium text-gray-light-700">{airline}</p>
      )}

      <div className="flex flex-col gap-2">
        {/* Route */}
        <p className="text-sm font-semibold text-gray-light-900">{route}</p>

        {/* Times */}
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-gray-light-900">
          <span>
            {journeyTypeLabel && `${journeyTypeLabel} · `}
            {departureLabel}
          </span>
          <span>&bull;</span>
          <span>{arrivalLabel}</span>
        </div>

        {/* Details */}
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-gray-light-600">
          <span>{stopsLabel}</span>
          <span>&bull;</span>
          <span>Durée : {durationLabel}</span>
          {travelClass && (
            <>
              <span>&bull;</span>
              <span>Classe : {travelClass}</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function PassengerCard({
  firstName,
  lastName,
  birthDate,
  gender,
  type,
}: PassengerCardProps) {
  const resolvedType = type ?? (birthDate ? derivePassengerType(birthDate) : null)
  const typeLabel = resolvedType ? (PASSENGER_TYPE_DISPLAY.get(resolvedType) ?? null) : null
  const genderLabel = gender ? (GENDER_LABELS_FULL.get(gender) ?? null) : null
  const birthDateLabel = birthDate ? formatDateShort(birthDate) : null

  const metaItems: string[] = []
  if (typeLabel) metaItems.push(typeLabel)
  if (genderLabel) metaItems.push(genderLabel)
  if (birthDateLabel) metaItems.push(birthDateLabel)

  return (
    <div className="flex items-start gap-4">
      <UserCircle className="size-6 shrink-0 text-gray-light-600" />
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-light-700">
          <Name>
            {normalizeFirstName(firstName)} {normalizeLastName(lastName)}
          </Name>
        </p>
        {metaItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-gray-light-600">
            {metaItems.map((item, i) => (
              <span key={i}>
                {i > 0 && <span className="mr-2">&bull;</span>}
                {item}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── TicketConditions ──

export function TicketConditions({ conditions, debugRule, infantCount }: TicketConditionsProps) {
  // MOD-INFANT : mention du frais bébé, uniquement si le dossier compte un bébé
  const hasInfant = (infantCount ?? 0) > 0

  return (
    <div className="flex flex-col gap-1.5">
      {/* Exchangeable */}
      <div className="flex items-start gap-3">
        {conditions.exchangeable.allowed ? (
          <Check className="mt-0.5 size-3 shrink-0 text-success-600" />
        ) : (
          <X className="mt-0.5 size-3 shrink-0 text-danger" />
        )}
        <ConditionText
          allowed={conditions.exchangeable.allowed}
          feeDescription={conditions.exchangeable.feeDescription}
          hasInfant={hasInfant}
          label="Billet modifiable"
        />
      </div>

      {/* Refundable */}
      <div className="flex items-start gap-3">
        {conditions.refundable.allowed ? (
          <Check className="mt-0.5 size-3 shrink-0 text-success-600" />
        ) : (
          <X className="mt-0.5 size-3 shrink-0 text-danger" />
        )}
        <ConditionText
          allowed={conditions.refundable.allowed}
          feeDescription={conditions.refundable.feeDescription}
          hasInfant={hasInfant}
          label="Billet remboursable sans justificatif"
        />
      </div>

      {debugRule && (
        <div className="mt-2 rounded border border-teal-200 bg-teal-50 p-2 font-mono text-[10px] leading-3 text-teal-800">
          <span className="font-bold">Règle appliquée:</span> {debugRule.ruleCode} (
          <button
            className="cursor-pointer hover:underline"
            onClick={() => {
              void navigator.clipboard.writeText(debugRule.ruleId).then(() => {
                toast.info('ID copié')
              })
            }}
            title="Cliquez pour copier l'ID"
          >
            id: {debugRule.ruleId}
          </button>
          ) - {debugRule.ruleSource}
        </div>
      )}
    </div>
  )
}

function ConditionText({
  allowed,
  feeDescription,
  hasInfant = false,
  label,
}: {
  allowed: boolean
  feeDescription: string
  hasInfant?: boolean
  label: string
}) {
  const parts = formatConditionText({
    adultFee: hasInfant ? parseFeeAmountFromDescription(feeDescription) : null,
    allowed,
    feeDescription,
    label,
  })
  return (
    <span className="text-xs leading-4 text-gray-light-900">
      {parts.map((part, i) =>
        part.bold ? (
          <strong key={i} className="font-bold">
            {part.text}
          </strong>
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </span>
  )
}
